import React, { useState, useEffect } from 'react';
import { theme } from '../../styles/theme';
import Button from './Button';
import { api } from '../../lib/api';

interface HeaderProps {
  user: { email: string; id: string };
  onLogout: () => void;
  onOpenYouTubeAPI: () => void;
  onOpenCreatorAssistant: () => void;
  onOpenStripePayments: () => void;
  onOpenCreateLink: () => void;
}

const Header: React.FC<HeaderProps> = ({
  user,
  onLogout,
  onOpenYouTubeAPI,
  onOpenCreatorAssistant,
  onOpenStripePayments,
  onOpenCreateLink
}) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showIntegrations, setShowIntegrations] = useState(false);
  const [isOnWaitlist, setIsOnWaitlist] = useState(false);
  const [joiningWaitlist, setJoiningWaitlist] = useState(false);
  const [waitlistSuccess, setWaitlistSuccess] = useState(false);

  // Check waitlist status on mount
  useEffect(() => {
    const checkWaitlistStatus = async () => {
      try {
        const response = await api.integrations.checkWaitlistStatus();
        setIsOnWaitlist(response.onWaitlist);
      } catch (error) {
        console.error('Error checking waitlist status:', error);
      }
    };
    checkWaitlistStatus();
  }, []);

  const handleJoinWaitlist = async () => {
    setJoiningWaitlist(true);
    try {
      const response = await api.integrations.joinWaitlist();
      setIsOnWaitlist(true);
      setWaitlistSuccess(true);
      console.log(response.message);
    } catch (error) {
      console.error('Error joining waitlist:', error);
      alert('Failed to join waitlist. Please try again.');
    } finally {
      setJoiningWaitlist(false);
    }
  };

  const headerStyles: React.CSSProperties = {
    position: 'sticky',
    top: 0,
    zIndex: 1000,
    backgroundColor: theme.colors.surface,
    borderBottom: `1px solid ${theme.colors.border}`,
    backdropFilter: 'blur(10px)',
    background: 'rgba(255, 255, 255, 0.95)'
  };

  const containerStyles: React.CSSProperties = {
    maxWidth: '1280px',
    margin: '0 auto',
    padding: `${theme.spacing.md} ${theme.spacing.lg}`,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  };

  const logoStyles: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing.md
  };

  const navStyles: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing.md
  };

  const userInfoStyles: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing.sm,
    padding: `${theme.spacing.sm} ${theme.spacing.md}`,
    backgroundColor: theme.colors.background,
    borderRadius: theme.borderRadius.full,
    fontSize: theme.typography.sizes.sm,
    color: theme.colors.text.secondary
  };

  const avatarStyles: React.CSSProperties = {
    width: '32px',
    height: '32px',
    borderRadius: theme.borderRadius.full,
    backgroundColor: theme.colors.accent,
    color: theme.colors.primary,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: theme.typography.weights.semibold,
    fontSize: theme.typography.sizes.sm
  };

  return (
    <header style={headerStyles}>
      <div style={containerStyles}>
        <div style={logoStyles}>
          <img 
            src="/revenue-social-logo.svg" 
            alt="Revenue Social" 
            style={{
              height: '50px',
              width: 'auto'
            }}
          />
        </div>

        {/* Desktop Navigation */}
        <nav className="desktop-nav" style={navStyles}>
          <div style={userInfoStyles}>
            <div style={avatarStyles}>
              {user.email.charAt(0).toUpperCase()}
            </div>
            <span>{user.email}</span>
          </div>
          
          <Button
            variant="ghost"
            size="sm"
            onClick={onOpenCreatorAssistant}
          >
            AI Assistant
          </Button>

          <Button
            variant="primary"
            size="sm"
            onClick={onOpenCreateLink}
          >
            🔗 Create Link
          </Button>

          <Button
            variant="accent"
            size="sm"
            onClick={() => setShowIntegrations(true)}
          >
            Integrations
          </Button>
          
          <Button
            variant="secondary"
            size="sm"
            onClick={onLogout}
          >
            Logout
          </Button>
        </nav>

        {/* Mobile Menu Button */}
        <button
          className="mobile-nav"
          style={{
            display: 'none',
            background: 'none',
            border: 'none',
            fontSize: '24px',
            cursor: 'pointer'
          }}
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
        >
          ☰
        </button>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div style={{
          position: 'absolute',
          top: '100%',
          left: 0,
          right: 0,
          backgroundColor: theme.colors.surface,
          borderBottom: `1px solid ${theme.colors.border}`,
          padding: theme.spacing.lg,
          display: 'flex',
          flexDirection: 'column',
          gap: theme.spacing.md
        }}>
          <div style={userInfoStyles}>
            <div style={avatarStyles}>
              {user.email.charAt(0).toUpperCase()}
            </div>
            <span>{user.email}</span>
          </div>
          
          <Button
            variant="ghost"
            fullWidth
            onClick={onOpenCreatorAssistant}
          >
            AI Assistant
          </Button>

          <Button
            variant="primary"
            fullWidth
            onClick={onOpenCreateLink}
          >
            🔗 Create Link
          </Button>

          <Button
            variant="accent"
            fullWidth
            onClick={() => setShowIntegrations(true)}
          >
            Integrations
          </Button>
          
          <Button
            variant="secondary"
            fullWidth
            onClick={onLogout}
          >
            Logout
          </Button>
        </div>
      )}

      {/* Integrations Waitlist Popup */}
      {showIntegrations && (
        <div
          onClick={() => setShowIntegrations(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2000,
            padding: '20px'
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: 'white',
              borderRadius: '16px',
              padding: '40px',
              maxWidth: '500px',
              width: '100%',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
              textAlign: 'center'
            }}
          >
            {/* Close button */}
            <button
              onClick={() => setShowIntegrations(false)}
              style={{
                position: 'absolute',
                top: '16px',
                right: '16px',
                background: 'none',
                border: 'none',
                fontSize: '24px',
                cursor: 'pointer',
                color: theme.colors.text.secondary,
                borderRadius: '4px',
                width: '32px',
                height: '32px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'background-color 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#F3F4F6';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              ×
            </button>

            {/* Icon */}
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              marginBottom: '24px'
            }}>
              <div style={{
                width: '80px',
                height: '80px',
                borderRadius: '50%',
                backgroundColor: waitlistSuccess ? '#DCFCE7' : '#EFF6FF',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '40px',
                transition: 'background-color 0.3s ease'
              }}>
                {waitlistSuccess ? '✓' : '🔌'}
              </div>
            </div>

            {/* Title */}
            <h2 style={{
              margin: '0 0 16px 0',
              fontSize: theme.typography.sizes['2xl'],
              fontWeight: theme.typography.weights.bold,
              color: theme.colors.text.primary
            }}>
              {waitlistSuccess ? "You're on the List!" : "Integrations Coming Soon"}
            </h2>

            {/* Message */}
            <p style={{
              margin: '0 0 32px 0',
              fontSize: theme.typography.sizes.base,
              color: theme.colors.text.secondary,
              lineHeight: '1.6'
            }}>
              {waitlistSuccess
                ? `We'll notify you at ${user.email} when integrations launch!`
                : "We're working on powerful integrations to supercharge your workflow. Join the waitlist to be the first to know when they're available!"
              }
            </p>

            {/* Features List */}
            <div style={{
              backgroundColor: theme.colors.background,
              borderRadius: '12px',
              padding: '20px',
              marginBottom: '32px',
              textAlign: 'left'
            }}>
              <p style={{
                margin: '0 0 12px 0',
                fontSize: theme.typography.sizes.sm,
                fontWeight: theme.typography.weights.semibold,
                color: theme.colors.text.primary
              }}>
                Upcoming integrations:
              </p>
              <ul style={{
                margin: 0,
                padding: '0 0 0 20px',
                fontSize: theme.typography.sizes.sm,
                color: theme.colors.text.secondary,
                lineHeight: '2'
              }}>
                <li>CRM platforms (HubSpot, Salesforce)</li>
                <li>Email marketing (Mailchimp, ConvertKit)</li>
                <li>Analytics tools (Google Analytics, Mixpanel)</li>
                <li>Payment processors (PayPal, Square)</li>
                <li>And many more...</li>
              </ul>
            </div>

            {/* Action Button */}
            {!waitlistSuccess && (
              <>
                <Button
                  variant="primary"
                  size="md"
                  onClick={handleJoinWaitlist}
                  disabled={isOnWaitlist || joiningWaitlist}
                  style={{
                    width: '100%',
                    opacity: isOnWaitlist || joiningWaitlist ? 0.6 : 1,
                    cursor: isOnWaitlist || joiningWaitlist ? 'not-allowed' : 'pointer'
                  }}
                >
                  {joiningWaitlist ? 'Joining...' : isOnWaitlist ? 'Already on Waitlist' : 'Join the Waitlist'}
                </Button>

                <p style={{
                  margin: '16px 0 0 0',
                  fontSize: theme.typography.sizes.xs,
                  color: theme.colors.text.secondary
                }}>
                  We'll notify you via email when integrations launch
                </p>
              </>
            )}

            {waitlistSuccess && (
              <Button
                variant="secondary"
                size="md"
                onClick={() => {
                  setShowIntegrations(false);
                  setWaitlistSuccess(false);
                }}
                style={{
                  width: '100%'
                }}
              >
                Close
              </Button>
            )}
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;